// The week-calendar attendance surface for one training class, shared by the
// mobile view (inline in `MobileAttendanceView`) and the desktop
// `TrainingAttendanceDialog` (see `features/mobile-admin.md`). A Mon…Sun week
// strip with prev/next navigation; tapping a day lists the class's weekly
// slots for that weekday, and tapping a slot creates (or reopens) its session
// — a session is identified by its date plus the translated slot label, so
// every surface records into the same sessions — where everyone who attended
// is marked with Present/Absent toggles. The opened session can be deleted.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import CircularProgress from '@mui/material/CircularProgress';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EventIcon from '@mui/icons-material/Event';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type {
  Athlete,
  AttendanceRecord,
  AttendanceStatus,
  TrainingClass,
  TrainingClassEnrollment,
  TrainingSession,
  TrainingSlot,
} from '../domain';
import {
  isoDateOf,
  addDaysIso,
  dayOfMonthIso,
  summarizeAttendance,
  weekOfIso,
  weekdayOfIso,
} from '../domain';
import {
  createTrainingSession,
  deleteTrainingSession,
  fetchTrainingSessions,
  markTrainingAttendance,
} from '../data/apiClient';
import { useTranslation } from '../state/I18nContext';

interface AttendanceCalendarProps {
  /** The class whose sessions are recorded here. */
  trainingClass: TrainingClass;
  /** This class's live enrolments (who attendance is tracked for). */
  enrollments: TrainingClassEnrollment[];
  /** The company roster, to resolve enrolled athletes' names. */
  athletes: Athlete[];
  /** Whether the signed-in user may create/delete sessions and mark attendance. */
  canManage: boolean;
}

export function AttendanceCalendar({
  trainingClass,
  enrollments,
  athletes,
  canManage,
}: AttendanceCalendarProps) {
  const { t, localeCode } = useTranslation();

  const today = isoDateOf(new Date());
  const [day, setDay] = useState(today);
  const [sessions, setSessions] = useState<TrainingSession[]>([]);
  const [attendance, setAttendance] = useState<AttendanceRecord[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Draft marks for the selected session, keyed by athlete personal number.
  const [draft, setDraft] = useState<Record<string, AttendanceStatus>>({});

  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const snapshot = await fetchTrainingSessions(trainingClass.id);
      setSessions(snapshot.sessions);
      setAttendance(snapshot.attendance);
      setError(null);
    } catch (e: unknown) {
      setError(
        e instanceof Error ? e.message : tRef.current('admin.trainingClasses.errors.load'),
      );
    } finally {
      setLoading(false);
    }
  }, [trainingClass.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const week = useMemo(() => weekOfIso(day), [day]);

  const athleteById = useMemo(
    () => new Map(athletes.map((a) => [a.personalNumber, a])),
    [athletes],
  );

  const sessionsByDate = useMemo(() => {
    const map = new Map<string, TrainingSession[]>();
    for (const session of sessions) {
      const list = map.get(session.date) ?? [];
      list.push(session);
      map.set(session.date, list);
    }
    return map;
  }, [sessions]);

  // A session is identified by its date plus this slot label, so the same
  // occurrence reopens rather than duplicating — on every surface.
  const slotLabel = useCallback(
    (slot: TrainingSlot) =>
      `${tRef.current(`admin.trainingClasses.weekdays.${slot.day}`)} ${slot.startTime}–${slot.endTime}`,
    [],
  );

  // (Re)seed the attendance draft whenever the selected session (or the loaded
  // marks) change: existing marks fill in, unmarked enrolled athletes default
  // to ABSENT so a single save records the whole roster.
  useEffect(() => {
    if (selectedSessionId === null) {
      setDraft({});
      return;
    }
    const marks = new Map(
      attendance
        .filter((a) => a.trainingSessionId === selectedSessionId)
        .map((a) => [a.athleteId, a.status]),
    );
    const next: Record<string, AttendanceStatus> = {};
    for (const enrollment of enrollments) {
      next[enrollment.athleteId] = marks.get(enrollment.athleteId) ?? 'ABSENT';
    }
    setDraft(next);
  }, [selectedSessionId, attendance, enrollments]);

  const summaryByAthlete = useMemo(() => {
    const map = new Map<string, ReturnType<typeof summarizeAttendance>>();
    for (const enrollment of enrollments) {
      const records = attendance.filter((a) => a.athleteId === enrollment.athleteId);
      map.set(enrollment.athleteId, summarizeAttendance(records));
    }
    return map;
  }, [enrollments, attendance]);

  const presentCount = useCallback(
    (sessionId: number) =>
      attendance.filter(
        (a) => a.trainingSessionId === sessionId && a.status === 'PRESENT',
      ).length,
    [attendance],
  );

  // Tap a slot: reopen the session already recorded for this date + slot, or
  // create a fresh one, then select it for marking.
  const openSlot = async (slot: TrainingSlot) => {
    if (busy) return;
    const label = slotLabel(slot);
    const existing = sessions.find((s) => s.date === day && s.note === label);
    if (existing) {
      setSelectedSessionId(existing.id);
      return;
    }
    if (!canManage) return;
    setBusy(true);
    setError(null);
    try {
      const snapshot = await createTrainingSession(trainingClass.id, {
        date: day,
        note: label,
      });
      setSessions(snapshot.sessions);
      setAttendance(snapshot.attendance);
      const created = snapshot.sessions.find((s) => s.date === day && s.note === label);
      setSelectedSessionId(created?.id ?? null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('admin.trainingClasses.errors.session'));
    } finally {
      setBusy(false);
    }
  };

  const removeSession = async (session: TrainingSession) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const snapshot = await deleteTrainingSession(session.id);
      setSessions(snapshot.sessions);
      setAttendance(snapshot.attendance);
      setSelectedSessionId(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('admin.trainingClasses.errors.session'));
    } finally {
      setBusy(false);
    }
  };

  const saveAttendance = async () => {
    if (selectedSessionId === null || busy) return;
    setBusy(true);
    setError(null);
    try {
      const records = enrollments.map((e) => ({
        athleteId: e.athleteId,
        status: draft[e.athleteId] ?? 'ABSENT',
      }));
      const snapshot = await markTrainingAttendance(selectedSessionId, records);
      setSessions(snapshot.sessions);
      setAttendance(snapshot.attendance);
      setSaved(true);
    } catch (e: unknown) {
      setError(
        e instanceof Error ? e.message : t('admin.trainingClasses.errors.attendance'),
      );
    } finally {
      setBusy(false);
    }
  };

  // Formats "July 2026" for the week header in the active locale. The ISO date
  // parses as UTC midnight, so format in UTC to keep the month stable.
  const monthLabel = useMemo(
    () =>
      new Intl.DateTimeFormat(localeCode, {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      }).format(new Date(day)),
    [localeCode, day],
  );

  const selectDay = (next: string) => {
    setDay(next);
    setSelectedSessionId(null);
  };

  const weekday = weekdayOfIso(day);
  const daySlots = trainingClass.schedule.filter((s) => s.day === weekday);
  const daySlotLabels = new Set(daySlots.map(slotLabel));
  // Sessions on this date recorded outside the weekly slots (e.g. added under
  // another UI language, or before a schedule change) still surface here.
  const extraSessions = (sessionsByDate.get(day) ?? []).filter(
    (s) => !daySlotLabels.has(s.note),
  );
  const selected = sessions.find((s) => s.id === selectedSessionId) ?? null;

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}

      {/* Week header — month + navigation. */}
      <Stack direction="row" alignItems="center" spacing={1}>
        <IconButton
          aria-label={t('admin.mobile.attendance.prevWeek')}
          onClick={() => selectDay(addDaysIso(day, -7))}
        >
          <ChevronLeftIcon />
        </IconButton>
        <Typography variant="subtitle1" sx={{ flexGrow: 1, textAlign: 'center' }}>
          {monthLabel}
        </Typography>
        <IconButton
          aria-label={t('admin.mobile.attendance.nextWeek')}
          onClick={() => selectDay(addDaysIso(day, 7))}
        >
          <ChevronRightIcon />
        </IconButton>
      </Stack>

      {/* The Mon…Sun strip. A dot flags days the class meets or has recorded. */}
      <Stack direction="row" spacing={0.5}>
        {week.map((date) => {
          const isSelected = date === day;
          const isToday = date === today;
          const hasSession = (sessionsByDate.get(date) ?? []).length > 0;
          const hasSlot = trainingClass.schedule.some(
            (s) => s.day === weekdayOfIso(date),
          );
          return (
            <ButtonBase
              key={date}
              aria-label={date}
              aria-pressed={isSelected}
              onClick={() => selectDay(date)}
              sx={{
                flex: 1,
                flexDirection: 'column',
                py: 1,
                borderRadius: 2,
                border: 1,
                borderColor: isSelected
                  ? 'primary.main'
                  : isToday
                    ? 'text.secondary'
                    : 'divider',
                bgcolor: isSelected ? 'primary.main' : 'transparent',
                color: isSelected ? 'primary.contrastText' : 'text.primary',
              }}
            >
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                {t(`admin.trainingClasses.weekdays.${weekdayOfIso(date)}`)}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {dayOfMonthIso(date)}
              </Typography>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  mt: 0.5,
                  borderRadius: '50%',
                  bgcolor: hasSession
                    ? isSelected
                      ? 'primary.contrastText'
                      : 'success.main'
                    : hasSlot
                      ? isSelected
                        ? 'primary.contrastText'
                        : 'text.disabled'
                      : 'transparent',
                }}
              />
            </ButtonBase>
          );
        })}
      </Stack>

      {day !== today && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Chip
            size="small"
            variant="outlined"
            label={t('admin.mobile.attendance.today')}
            onClick={() => selectDay(today)}
          />
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* The selected day's slots — tap to record (or revisit) a session. */}
          {daySlots.length === 0 && extraSessions.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              {t('admin.mobile.attendance.noSlotsForDay')}
            </Typography>
          ) : (
            <Stack spacing={1}>
              {daySlots.map((slot, i) => {
                const label = slotLabel(slot);
                const session = sessions.find((s) => s.date === day && s.note === label);
                const active = session !== undefined && session.id === selectedSessionId;
                return (
                  <Paper
                    key={i}
                    component={ButtonBase}
                    variant="outlined"
                    disabled={busy || (session === undefined && !canManage)}
                    onClick={() => void openSlot(slot)}
                    sx={{
                      p: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      gap: 1,
                      textAlign: 'left',
                      width: '100%',
                      borderColor: active ? 'primary.main' : undefined,
                    }}
                  >
                    <EventIcon fontSize="small" color={session ? 'success' : 'action'} />
                    <Typography variant="body1" sx={{ flexGrow: 1 }}>
                      {`${slot.startTime}–${slot.endTime}`}
                    </Typography>
                    {session ? (
                      <Chip
                        size="small"
                        color="success"
                        variant="outlined"
                        label={t('admin.trainingClasses.attendance.presentCount', {
                          count: presentCount(session.id),
                        })}
                      />
                    ) : (
                      canManage && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={t('admin.mobile.attendance.tapToRecord')}
                        />
                      )
                    )}
                  </Paper>
                );
              })}
              {extraSessions.map((session) => (
                <Paper
                  key={session.id}
                  component={ButtonBase}
                  variant="outlined"
                  disabled={busy}
                  onClick={() => setSelectedSessionId(session.id)}
                  sx={{
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: 1,
                    textAlign: 'left',
                    width: '100%',
                    borderColor: session.id === selectedSessionId ? 'primary.main' : undefined,
                  }}
                >
                  <EventIcon fontSize="small" color="success" />
                  <Typography variant="body1" sx={{ flexGrow: 1 }} noWrap>
                    {session.note || session.date}
                  </Typography>
                  <Chip
                    size="small"
                    color="success"
                    variant="outlined"
                    label={t('admin.trainingClasses.attendance.presentCount', {
                      count: presentCount(session.id),
                    })}
                  />
                </Paper>
              ))}
            </Stack>
          )}

          {/* Marking panel for the opened session. */}
          {selected && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <EventIcon fontSize="small" color="primary" />
                <Typography variant="subtitle1" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
                  {selected.note ? `${selected.note} · ${selected.date}` : selected.date}
                </Typography>
                {canManage && (
                  <Tooltip title={t('admin.trainingClasses.attendance.deleteSession')}>
                    <span>
                      <IconButton
                        size="small"
                        disabled={busy}
                        aria-label={t('admin.trainingClasses.attendance.deleteSessionAria', {
                          date: selected.date,
                        })}
                        onClick={() => void removeSession(selected)}
                      >
                        <DeleteOutlineIcon fontSize="inherit" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Stack>

              {enrollments.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('admin.trainingClasses.attendance.noEnrolled')}
                </Typography>
              ) : (
                <Stack spacing={1.25}>
                  {canManage && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        size="small"
                        startIcon={<DoneAllIcon />}
                        disabled={busy}
                        onClick={() =>
                          setDraft(
                            Object.fromEntries(
                              enrollments.map((e) => [e.athleteId, 'PRESENT']),
                            ),
                          )
                        }
                      >
                        {t('admin.mobile.attendance.markAllPresent')}
                      </Button>
                    </Box>
                  )}
                  {enrollments.map((enrollment) => {
                    const athlete = athleteById.get(enrollment.athleteId);
                    const summary = summaryByAthlete.get(enrollment.athleteId);
                    const status = draft[enrollment.athleteId] ?? 'ABSENT';
                    return (
                      <Stack
                        key={enrollment.id}
                        direction="row"
                        alignItems="center"
                        spacing={1}
                      >
                        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                          <Typography variant="body2" noWrap>
                            {athlete?.fullName ?? enrollment.athleteId}
                          </Typography>
                          {summary && summary.marked > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              {t('admin.trainingClasses.attendance.summary', {
                                present: summary.present,
                                total: summary.marked,
                                percent: Math.round(summary.rate * 100),
                              })}
                            </Typography>
                          )}
                        </Box>
                        <ToggleButtonGroup
                          exclusive
                          size="small"
                          value={status}
                          disabled={!canManage || busy}
                          onChange={(_e, next: AttendanceStatus | null) => {
                            if (next === null) return;
                            setDraft((prev) => ({
                              ...prev,
                              [enrollment.athleteId]: next,
                            }));
                          }}
                        >
                          <ToggleButton value="PRESENT" color="success">
                            {t('admin.trainingClasses.attendance.present')}
                          </ToggleButton>
                          <ToggleButton value="ABSENT" color="error">
                            {t('admin.trainingClasses.attendance.absent')}
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Stack>
                    );
                  })}
                  {canManage && (
                    <Button
                      fullWidth
                      variant="contained"
                      disabled={busy}
                      onClick={() => void saveAttendance()}
                    >
                      {t('admin.trainingClasses.attendance.save')}
                    </Button>
                  )}
                </Stack>
              )}
            </Paper>
          )}
        </>
      )}

      <Snackbar
        open={saved}
        autoHideDuration={2500}
        onClose={() => setSaved(false)}
        message={t('admin.mobile.attendance.saved')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        // Clears the mobile view's fixed bottom navigation on phone screens.
        sx={{ bottom: { xs: 72, sm: 24 } }}
      />
    </Stack>
  );
}
